import { IPromise } from 'angular';
import * as _ from 'underscore';

import { CommonStyles } from '../../common/react/CommonStyles';
import { ISiteRoleFilter } from '../filters/SiteRoleFilter';
import { IActionContext } from '../helpers/ActionContext';
import { ResultHelper } from '../helpers/ResultHelper';
import { ISiteRolePickerMenuScope, SiteRolePickerMenuHelper } from '../helpers/SiteRolePickerMenuHelper';
import { L10n } from '../L10n/L10n';
import { IGroupDetailsInfo } from '../models/IGroupDetailsInfo';
import { IGroupBasicInfo, IGroupInfo } from '../models/IGroupInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Groups } from '../resources/Groups';
import { SiteRoleOption, SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { SiteRolesResource } from '../resources/SiteRoles';
import { IResult } from '../ServerApi/types/IResponse';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ServerService } from './ServerService';
import { ToasterService, ToastType } from './ToasterService';

export interface ISetGroupsMinimumSiteRoleDialogScope extends ISiteRolePickerMenuScope {
  description: string;
  siteRole: string;
  siteRoleOptions: string[];
  setSiteRole: (role: string) => void;
}

export class UpdateGroupsMinimumSiteRoleAction {

  public static $inject = [
    '$translate',
    '$q',
    '$filter',
    'Groups',
    'ServerService',
    'SiteRoles',
    'SiteRoleOptionsResource',
    'ToasterService',
    'ConfirmActionDialog'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private $q: ng.IQService,
    private $filter: ng.IFilterService,
    private Groups: Groups,
    private server: ServerService,
    private siteRoles: SiteRolesResource,
    private siteRoleOptionsResource: SiteRoleOptionsResource,
    private toaster: ToasterService,
    private confirmActionDialog: ConfirmActionDialog
  ) { }

  public executeInPlace(group: IGroupDetailsInfo, siteRole: string) {
    const groupSet = Set.fromIdItemArray([group]);

    return this.Groups.updateGroupsMinimumSiteRole([group.id], siteRole)
      .then(ResultHelper.rejectResultOnError(this.$q))
      .then(() => this.handleSuccessResult(groupSet, siteRole))
      .catch((result: IResult) => this.handleErrorResult(groupSet, siteRole, result));
  }

  public showUpdateGroupsMinimumSiteRoleDialog(
    groupsToSet: Set<IGroupInfo>,
    context: IActionContext): IPromise<any> {

    const groupsOnlyAD = groupsToSet.filter(Groups.isActiveDirectory);

    if(groupsOnlyAD.size() === 0) {
      // No active directory groups were selected, display an error message
      return this.confirmActionDialog.open({
        scope: context.scope,
        title: this.$translate.instant('minimumSiteRole'),
        bodyText: this.$translate.instant('minimumSiteRoleNoADGroups')
      }).result;
    }

    const dialogScope = <ISetGroupsMinimumSiteRoleDialogScope> context.scope.$new();

    dialogScope.siteRole = (groupsOnlyAD.size() === 1) ? groupsOnlyAD.first().minimumSiteRole : SiteRoles.Unlicensed;

    dialogScope.siteRoleOptions = this.siteRoles.optionsForCurrentUserExcept(SiteRoles.ServerAdministrator);
    dialogScope.setSiteRole = (role: string) => {
      dialogScope.siteRole = role;
    };

    const onSiteRoleSelected = (roleOption: SiteRoleOption) => {
      dialogScope.$apply(() => {
        dialogScope.siteRoleSelectorProps.selectedRole = roleOption;
        dialogScope.setSiteRole(roleOption.role);
      });
    };

    const GroupsSiteRoleDropdownWidth = 260;

    SiteRolePickerMenuHelper.setupPickerMenuScope(dialogScope, this.$translate, this.server, this.siteRoleOptionsResource, dialogScope.siteRole, onSiteRoleSelected, true);
    dialogScope.siteRoleSelectorProps.style = { width: GroupsSiteRoleDropdownWidth - CommonStyles.Sizes.DefaultSpace - (2 * CommonStyles.Sizes.TextBoxBorderSize) };

    dialogScope.description = this.$translate.instant('confirmSetGroupsMinimumSiteRoleDesc', {
      GROUP_NAME: L10n.elideIfLong(groupsOnlyAD.first().name),
      GROUP_COUNT: groupsOnlyAD.size()
    }, L10n.Interpolations.Count);

    const dialogInstance = this.confirmActionDialog.open({
      title: this.$translate.instant('confirmSetGroupsMinimumSiteRoleTitle'),
      bodyTemplateUrl: 'confirmActionDialogInputRole.html',
      scope: dialogScope,
      confirmDisabled: () => false, // 'confirm' is always enabled for this action.
      actionName: this.$translate.instant('confirmSetGroupsMinimumSiteRoleButton', {
        ITEM_COUNT: groupsOnlyAD.size()
      }, L10n.Interpolations.Count),
      action: (dialogInstance: IConfirmActionDialogInstance) => this.setGroupsSiteRoles(groupsOnlyAD, dialogScope.siteRole)
    });

    dialogInstance.result.finally(() => dialogScope.$destroy());

    return dialogInstance.result;
  }

  private setGroupsSiteRoles(selectedGroups: Set<IGroupInfo>, siteRole: string): IPromise<any> {
    const groups = _.map(selectedGroups.toArray(), (group: IGroupInfo) => group.id);

    return this.Groups.updateGroupsMinimumSiteRole(groups, siteRole)
      .then(ResultHelper.rejectResultOnError(this.$q))
      .then(() => this.handleSuccessResult(selectedGroups, siteRole))
      .catch((result: IResult) => this.handleErrorResult(selectedGroups, siteRole, result));
  }

  private handleSuccessResult(groups: Set<IGroupBasicInfo>, siteRole: string) {
    this.toaster.pop({
      type: ToastType.Info,
      text: this.$translate.instant('updateGroupsMinimumSiteRoleSuccess', {
        GROUP_NAME: L10n.elideIfLong(groups.first().name),
        COUNT: groups.size(),
        SITE_ROLE: this.$filter<ISiteRoleFilter>('siteRole')(siteRole)
      }, L10n.Interpolations.Count)
    });
  }

  private handleErrorResult(groups: Set<IGroupBasicInfo>, siteRole: string, result: IResult) {
    this.toaster.pop({
      type: ToastType.Error,
      text: this.$translate.instant('updateGroupsMinimumSiteRoleError', {
        GROUP_NAME: L10n.elideIfLong(groups.first().name),
        COUNT: groups.size(),
        SITE_ROLE: this.$filter<ISiteRoleFilter>('siteRole')(siteRole)
      }, L10n.Interpolations.Count)
    });
    return this.$q.reject(result); // for chaining errors
  }

}

Module.instance().service('UpdateGroupsMinimumSiteRoleAction', UpdateGroupsMinimumSiteRoleAction);
